/**
 * Cookie consent management with Google Consent Mode v2 and PostHog.
 *
 * Preferences are persisted in localStorage (granular per-category) and
 * mirrored to a `_consent` cookie (coarse level for Server Components).
 *
 * When the user interacts with the cookie banner or settings modal,
 * preferences are updated and both gtag consent and PostHog capturing
 * state are updated in real-time.
 *
 * Advertising (marketing) consent is additionally persisted to the customer's
 * Medusa metadata (`health_consent`) so it can propagate to downstream systems
 * (e.g. Braze connector via EventBridge enrichment). The whole site is
 * healthcare, so marketing consent is consent to health data in the marketing
 * profile; the field name is retained for cross-repo compatibility (verified in
 * `tests/unit/analytics-health-consent-sync.test.ts`).
 */

import { brazeOptOut } from './braze';
import {
  CONSENT_COOKIE_NAME,
  CONSENT_GRANTED_EVENT,
  CONSENT_STORAGE_KEY,
  CONSENT_UPDATED_EVENT,
  MARKETING_CONSENT_GRANTED_EVENT,
} from './constants';
import { gtag } from './gtag';
import type { ConsentLevel, ConsentPreferences, ConsentState } from './types';

// ---------------------------------------------------------------------------
// Google Consent Mode mapping
// ---------------------------------------------------------------------------

/** Build a ConsentState object from user preferences. */
const preferencesToConsentState = (
  prefs: ConsentPreferences,
): ConsentState => ({
  analytics_storage: prefs.analytics ? 'granted' : 'denied',
  ad_storage: prefs.advertising ? 'granted' : 'denied',
  ad_user_data: prefs.advertising ? 'granted' : 'denied',
  ad_personalization: prefs.advertising ? 'granted' : 'denied',
});

// ---------------------------------------------------------------------------
// localStorage persistence
// ---------------------------------------------------------------------------

/** Read stored consent preferences (returns null if none stored). */
export const getConsentPreferences = (): ConsentPreferences | null => {
  if (typeof window === 'undefined') {
    return null;
  }

  try {
    const raw = localStorage.getItem(CONSENT_STORAGE_KEY);

    if (!raw) {
      return null;
    }

    const parsed = JSON.parse(raw) as Partial<ConsentPreferences> & {
      health?: boolean;
    };

    return parsed as ConsentPreferences;
  } catch {
    return null;
  }
};

/** Persist consent preferences to localStorage. */
export const setConsentPreferences = (prefs: ConsentPreferences): void => {
  if (typeof window === 'undefined') {
    return;
  }

  try {
    localStorage.setItem(CONSENT_STORAGE_KEY, JSON.stringify(prefs));
  } catch {
    // Storage full or blocked — fail silently
  }
};

// ---------------------------------------------------------------------------
// _consent cookie (coarse signal for Server Components)
// ---------------------------------------------------------------------------

/**
 * Derive the coarse consent level from granular preferences.
 *
 * - `"all"` — every non-essential category is granted
 * - `"essential"` — every non-essential category is denied
 * - `"custom"` — mixed selection
 */
const deriveConsentLevel = (prefs: ConsentPreferences): ConsentLevel => {
  const allGranted = prefs.preferences && prefs.analytics && prefs.advertising;
  const allDenied =
    !prefs.preferences && !prefs.analytics && !prefs.advertising;

  if (allGranted) {
    return 'all';
  }

  if (allDenied) {
    return 'essential';
  }

  return 'custom';
};

/**
 * Set the consent cookie so Server Components can read consent level.
 *
 * This is an essential cookie (it records the user's cookie preference
 * itself), so it's always permitted regardless of consent level.
 * 1-year expiry matches typical consent preference retention.
 */
const setConsentCookie = (level: ConsentLevel): void => {
  if (typeof document === 'undefined') {
    return;
  }

  const maxAge = 60 * 60 * 24 * 365; // 1 year

  if (typeof cookieStore !== 'undefined') {
    cookieStore
      .set({
        name: CONSENT_COOKIE_NAME,
        value: level,
        // Cookie Store API expects an epoch timestamp in milliseconds,
        // unlike the seconds-based max-age used in the fallback below.
        expires: Date.now() + maxAge * 1000,
        sameSite: 'lax',
        path: '/',
      })
      .catch(() => {
        // Async failure — fail silently.
      });
  } else {
    // Safari < 18.4 and older iOS don't have the Cookie Store API.
    // biome-ignore lint/suspicious/noDocumentCookie: Cookie Store API fallback for browsers without it
    document.cookie = `${CONSENT_COOKIE_NAME}=${level}; max-age=${maxAge}; path=/; samesite=lax`;
  }
};

// ---------------------------------------------------------------------------
// Third-party consent updates
// ---------------------------------------------------------------------------

/** Push a consent update to gtag. */
export const updateGtagConsent = (prefs: ConsentPreferences): void => {
  const state = preferencesToConsentState(prefs);

  gtag('consent', 'update', state);
};

/** Sync PostHog capturing state with consent preferences. */
const updatePostHogConsent = (analyticsGranted: boolean): void => {
  try {
    // posthog-js is loaded in instrumentation-client.ts. We import it
    // dynamically to avoid pulling the module into server bundles and to
    // tolerate the case where the SDK isn't initialised (e.g. no token).
    void import('posthog-js').then(({ default: posthog }) => {
      if (!posthog.__loaded) {
        return;
      }

      if (analyticsGranted) {
        posthog.opt_in_capturing();
        // User just clicked "Accept" — that click is the first interaction,
        // so start recording immediately instead of waiting for the lazy timer.
        posthog.startSessionRecording();

        return;
      }

      posthog.opt_out_capturing();
    });
  } catch {
    // PostHog not available — fail silently
  }
};

/**
 * Notify the GoogleAnalytics component that consent has been granted
 * so it can render the gtag.js <Script> tag using the existing React/Next.js flow.
 *
 * The dataLayer queue ensures any events fired before the script loads
 * are processed once it arrives.
 */
const notifyGtagConsentGranted = (): void => {
  if (typeof window === 'undefined') {
    return;
  }

  window.dispatchEvent(new Event(CONSENT_GRANTED_EVENT));
};

/** Dispatch a generic "consent updated" event for any preference change. */
const notifyConsentUpdated = (): void => {
  if (typeof window === 'undefined') {
    return;
  }

  window.dispatchEvent(new Event(CONSENT_UPDATED_EVENT));
};

/**
 * Notify the Braze SDK loader that marketing consent has been granted
 * so it can dynamically import `@braze/web-sdk` and initialise.
 *
 * Mirrors `notifyGtagConsentGranted` for gtag.js — ensures the Braze
 * bundle is never fetched until the user explicitly opts in to
 * marketing tracking.
 */
const notifyMarketingConsentGranted = (): void => {
  if (typeof window === 'undefined') {
    return;
  }

  window.dispatchEvent(new Event(MARKETING_CONSENT_GRANTED_EVENT));
};

// ---------------------------------------------------------------------------
// Advertising consent → customer metadata
// ---------------------------------------------------------------------------

/**
 * Sync the customer's marketing/advertising consent into
 * `customer.metadata.health_consent` (the cross-repo Article-9 gate).
 * Sourced from advertising consent as it's a combined consent.
 *
 * The target field name (`health_consent`) is deliberately retained for
 * cross-repo compatibility; only this local function name has changed.
 *
 * This is fire-and-forget — we don't want consent UI to block on a
 * network request. The server action handles auth; if the user isn't
 * logged in the call will silently fail (which is fine — the preference
 * will be synced when they next log in or during checkout).
 *
 * @param advertisingConsent - Whether the user has granted advertising/marketing consent.
 */
const syncAdvertisingConsentToCustomer = (
  advertisingConsent: boolean,
): void => {
  // Dynamic import to avoid pulling the server action into client bundles
  // at module evaluation time.
  void import('@lib/data/customer').then(({ updateHealthConsent }) => {
    updateHealthConsent(advertisingConsent).catch(() => {
      // Not logged in or network error — fail silently.
      // localStorage is the primary store; customer metadata is a
      // best-effort propagation for downstream systems.
    });
  });
};

// ---------------------------------------------------------------------------
// Core consent actions (called by banner / settings modal)
// ---------------------------------------------------------------------------

/**
 * Apply consent preferences, updating all stores and third-party state.
 *
 * This is the single point of truth for applying consent changes —
 * `acceptAll`, `rejectNonEssential`, and `saveCustomPreferences` all
 * delegate here.
 */
const applyPreferences = (prefs: ConsentPreferences): void => {
  setConsentPreferences(prefs);
  setConsentCookie(deriveConsentLevel(prefs));
  updateGtagConsent(prefs);
  updatePostHogConsent(prefs.analytics);

  if (prefs.analytics) {
    notifyGtagConsentGranted();
  }

  // Braze is gated on **marketing** (advertising) consent, not analytics
  // consent. Per MWP-449 (most-compliant interpretation): no Braze SDK
  // loading, no events, no identify until the user explicitly opts in
  // to marketing tracking.
  if (prefs.advertising) {
    notifyMarketingConsentGranted();
  } else {
    // Withdrawing marketing consent must immediately stop Braze: disable
    // the SDK, wipe locally-stored device/anonymous IDs and queued events.
    brazeOptOut();
  }

  // Notify all listeners that consent preferences changed (any category).
  notifyConsentUpdated();

  // Sync advertising consent to customer metadata (fire-and-forget)
  syncAdvertisingConsentToCustomer(prefs.advertising);
};

/** Accept all cookies: preferences + analytics + advertising. */
export const acceptAll = (): void => {
  applyPreferences({
    preferences: true,
    analytics: true,
    advertising: true,
    timestamp: Date.now(),
  });
};

/** Reject non-essential cookies. */
export const rejectNonEssential = (): void => {
  applyPreferences({
    preferences: false,
    analytics: false,
    advertising: false,
    timestamp: Date.now(),
  });
};

/** Save a custom per-category selection from the settings modal. */
export const saveCustomPreferences = (
  preferences: boolean,
  analytics: boolean,
  advertising: boolean,
): void => {
  applyPreferences({
    preferences,
    analytics,
    advertising,
    timestamp: Date.now(),
  });
};

// ---------------------------------------------------------------------------
// Consent helpers (for analytics event gating)
// ---------------------------------------------------------------------------

/** Whether the user has granted marketing (advertising) consent. */
export const hasMarketingConsent = (): boolean => {
  const prefs = getConsentPreferences();

  if (prefs) {
    return prefs.advertising === true;
  }

  // Fallback: if localStorage is cleared/blocked but the consent cookie
  // is present as "all", treat advertising as consented.
  if (typeof document !== 'undefined') {
    const match = document.cookie.match(
      new RegExp(`(?:^|;\\s*)${CONSENT_COOKIE_NAME}=([^;]*)`),
    );

    return match?.[1] === 'all';
  }

  return false;
};

/** Whether the user has granted preferences (functional) consent. */
export const hasPreferencesConsent = (): boolean => {
  const prefs = getConsentPreferences();

  if (prefs) {
    return prefs.preferences === true;
  }

  if (typeof document !== 'undefined') {
    const match = document.cookie.match(
      new RegExp(`(?:^|;\\s*)${CONSENT_COOKIE_NAME}=([^;]*)`),
    );

    return match?.[1] === 'all';
  }

  return false;
};

/** Whether the user has granted analytics (statistics) consent. */
export const hasAnalyticsConsent = (): boolean => {
  const prefs = getConsentPreferences();

  if (prefs) {
    return prefs.analytics === true;
  }

  if (typeof document !== 'undefined') {
    const match = document.cookie.match(
      new RegExp(`(?:^|;\\s*)${CONSENT_COOKIE_NAME}=([^;]*)`),
    );

    return match?.[1] === 'all';
  }

  return false;
};

// ---------------------------------------------------------------------------
// Choice detection
// ---------------------------------------------------------------------------

/** Whether the user has already made a consent choice (checks cookie, then localStorage). */
export const hasConsentChoice = (): boolean => {
  if (typeof document !== 'undefined') {
    const match = document.cookie.match(
      new RegExp(`(?:^|;\\s*)${CONSENT_COOKIE_NAME}=([^;]*)`),
    );

    if (match) {
      return true;
    }
  }

  return getConsentPreferences() !== null;
};
